// =============================================================
// SystemDiagOverlay — Runtime diagnostics panel
// =============================================================
// Shows a "system diagnostic" overlay with build metadata.
// Triggered internally by the engagement tracker when a validated
// interaction sequence completes (see lib/easterEggs.ts).
//
// Not accessible from the UI. Internal-only.

import { useEffect, useRef, useState } from "react";

interface SystemDiagOverlayProps {
  open: boolean;
  onClose: () => void;
  variant?: "full" | "toast";
  who?: "jcfp" | "sad" | null;
}

export function SystemDiagOverlay({
  open,
  onClose,
  variant = "full",
  who,
}: SystemDiagOverlayProps) {
  const [visible, setVisible] = useState(false);
  const [showStars, setShowStars] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (open) {
      setVisible(true);
      setShowStars(true);
    } else {
      setShowStars(false);
      timeoutRef.current = setTimeout(() => setVisible(false), 500);
    }
    return () => clearTimeout(timeoutRef.current);
  }, [open]);

  if (!visible) return null;

  // "Toast" variant — small notification from keyboard shortcut
  if (variant === "toast") {
    const isJcfp = who === "jcfp";
    return (
      <div
        className={`fixed bottom-6 right-6 z-[9999] transition-all duration-500 ${open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        style={{ pointerEvents: open ? "auto" : "none" }}
      >
        <div
          className={`glass-strong rounded-2xl p-5 max-w-xs text-center cursor-pointer ${isJcfp ? "border-t-brand-400/50" : "border-t-critical-400/50"
            }`}
          onClick={onClose}
        >
          <div className="text-2xl mb-1">{isJcfp ? "👨‍💻" : "🧑‍💻"}</div>
          <div className="text-sm font-bold text-white">
            {isJcfp ? "Jan Carlo F. Perez" : "Seth Andrei Dimaano"}
          </div>
          <div className="text-[11px] text-slate-300 mt-1">
            {isJcfp ? "Lead Developer · Architect" : "Co-Developer · Engineer"}
          </div>
          <div className="text-[10px] text-slate-400 mt-2 italic">
            {isJcfp
              ? '"First, solve the problem. Then, write the code."'
              : '"Code is like humor. When you have to explain it, it\'s bad."'}
          </div>
        </div>
      </div>
    );
  }

  // "Full" variant — Konami code grand credits
  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-500 ${open ? "opacity-100" : "opacity-0"
        }`}
      onClick={onClose}
      style={{ pointerEvents: open ? "auto" : "none" }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" />

      {/* Stars */}
      {showStars && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {Array.from({ length: 50 }).map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white"
              style={{
                width: `${Math.random() * 3 + 1}px`,
                height: `${Math.random() * 3 + 1}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                opacity: Math.random() * 0.8 + 0.2,
                animation: `twinkle ${Math.random() * 3 + 2}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 3}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Credits card */}
      <div
        className={`relative z-10 max-w-md w-full mx-4 transition-all duration-700 ${open ? "scale-100 translate-y-0" : "scale-90 translate-y-8"
          }`}
      >
        <div className="glass-strong rounded-3xl p-8 text-center relative overflow-hidden">
          {/* Animated gradient border effect */}
          <div
            className="absolute inset-0 rounded-3xl opacity-50 pointer-events-none"
            style={{
              background:
                "linear-gradient(135deg, rgba(0,14,181,0.3), transparent 40%, transparent 60%, rgba(251,11,20,0.3))",
              animation: "gradient-shift 6s ease infinite",
              backgroundSize: "200% 200%",
            }}
          />

          {/* Content */}
          <div className="relative">
            <div className="text-4xl mb-3">🏆</div>
            <h2 className="text-2xl font-bold mb-1 text-white">
              IMPACT Pro Credits
            </h2>
            <p className="text-sm text-slate-400 mb-6">
              You found the secret! 🎉
              Thank you for guiding us through this project.
            </p>

            <div className="space-y-4">
              {/* Jan Carlo */}
              <div className="glass-subtle rounded-2xl p-4 text-left border-t border-t-brand-400/30">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                    style={{
                      background:
                        "linear-gradient(135deg, #000eb5, #3d49ff)",
                    }}
                  >
                    👨‍💻
                  </div>
                  <div>
                    <div className="text-white font-semibold">
                      Jan Carlo F. Perez
                    </div>
                    <div className="text-xs text-brand-200">
                      Lead Developer · System Architect
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-2 italic">
                  "The only way to do great work is to love what you do."
                </p>
              </div>

              {/* Seth Andrei */}
              <div className="glass-subtle rounded-2xl p-4 text-left border-t border-t-critical-400/30">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                    style={{
                      background:
                        "linear-gradient(135deg, #fb0b14, #ff545d)",
                    }}
                  >
                    🧑‍💻
                  </div>
                  <div>
                    <div className="text-white font-semibold">
                      Seth Andrei Dimaano
                    </div>
                    <div className="text-xs text-critical-200">
                      Co-Developer
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-2 italic">
                  "Simplicity is the soul of efficiency."
                </p>
              </div>
            </div>

            <div className="mt-6 text-[10px] text-slate-500 space-y-1">
              <p>Built with React · TypeScript · PHP · MySQL</p>
              <p>Liquid Glass UI · Vite · TailwindCSS</p>
              <p className="mt-2 text-slate-600">
                Press anywhere to close · {new Date().getFullYear()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
